// API Configuration
export const API_CONFIG = {
  BASE_URL:
    process.env.REACT_APP_API_BASE_URL ||
    (import.meta as any).env?.VITE_API_BASE_URL ||
    "https://api-giftcode.mhdpharma.com/api",
  ENDPOINTS: {
    AUTH: {
      LOGIN: "/auth/login",
      REGISTER: "/auth/register",
      ME: "/auth/me",
      CHANGE_PASSWORD: "/auth/change-password",
      LOGOUT: "/auth/logout",
      REFRESH: "/auth/refresh",
    },
    USERS: "/users",
    USER_BY_ID: (id: string) => `/users/${id}`,
    USER_PROFILE: "/users/me/profile",
    ROLES: "/roles",
    WIN_PRIZES: "/win-prizes",
    WIN_PRIZES_EXPORT: "/win-prizes/export",
    CAMPAIGNS_EXPORT_ALL: "/campaigns/export/all",
    CAMPAIGNS_EXPORT_NEW: "/campaigns/export/new",
    PASSWORD_RESET: {
      SEND_OTP: "/password-reset/send-otp",
      VERIFY_OTP: "/password-reset/verify-otp",
      RESET_PASSWORD: "/password-reset/reset-password",
      TEST_EMAIL: "/password-reset/test-email",
    },
    HEALTH: "/health",
    INFO: "/info",
    STATS: "/stats",
    STATUS: "/status",
    CORS_TEST: "/cors-test",
    VERIFY_CODE: "/verify-code",
    ERROR_MESSAGES: "/error-messages",
    ERROR_MESSAGE_BY_ID: (id: string) => `/error-messages/${id}`,
    CAMPAIGNS: "/campaigns",
    CAMPAIGNS_STAT_TOP: "/campaigns/statistical/top",
    CAMPAIGN_BY_ID: (id: string) => `/campaigns/${id}`,
    CAMPAIGN_STATUS: (id: string) => `/campaigns/status/${id}`,
    CAMPAIGN_PARTNERS: (id: string) => `/campaigns/${id}/partners`,
    CAMPAIGN_PARTNER_BY_ID: (id: string, partnerId: string) =>
      `/campaigns/${id}/partners/${partnerId}`,
    CODES: "/codes",
    CODES_EXPORT_NOT_USED: "/codes/export/not-used",
    CODES_DELETE: "/codes/delete",
    CODE_BY_ID: (id: string) => `/codes/${id}`,
    BATCHES: "/batchs",
    ACCESS: "/access",
    PARTNERS: "/partners",
    PARTNER_BY_ID: (id: string) => `/partners/${id}`,
    ALL_PARTNER_VOUCHERS: "/partners/vouchers",
    PARTNER_VOUCHERS: (id: string) => `/partners/${id}/vouchers`,
    PARTNER_VOUCHERS_IMPORT: (id: string) => `/partners/${id}/vouchers/import`,
    PARTNER_VOUCHER_REDEEM: (partnerId: string, voucherId: string) =>
      `/partners/${partnerId}/vouchers/${voucherId}/redeem`,
    PARTNER_LOCATIONS: (id: string) => `/partners/${id}/locations`,
    PARTNER_LOCATIONS_IMPORT: (id: string) => `/partners/${id}/locations/import`,
    PARTNER_LOCATION_BY_ID: (partnerId: string, locationId: string) =>
      `/partners/${partnerId}/locations/${locationId}`,
    VOUCHER_EXCHANGES: "/voucher-exchanges",
    VOUCHER_EXCHANGE_SWITCH_PARTNER: (id: string) =>
      `/voucher-exchanges/${id}/switch-partner`,
  },
};

// Helper function to build full API URLs
export const buildApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Types for API responses
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
  token: string;
  user: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    role_id: string;
    role_name: string;
    createdAt?: string;
  };
}

export interface GetUserResponse {
  success: boolean;
  user: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    role_id: string;
    role_name: string;
    createdAt: string;
  };
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  phone?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  role_id: string;
  role_name: string;
  createdAt?: string;
}

export interface UpdateProfileRequest {
  name: string;
  email: string;
  phone: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  message: string;
  data: {
    name: string;
    email: string;
    phone: string;
    role_id: number;
    role_name: string;
    createdAt: string;
  };
}

export interface RefreshTokenResponse {
  success: boolean;
  message: string;
  token: string;
  user: {
    name: string;
    email: string;
    phone: string;
    role_id: number;
    role_name: string;
    createdAt: string;
  };
}

// User Management Interfaces
export interface UserManagement {
  _id: string;
  name: string;
  email: string;
  phone: string;
  role_id: string;
  role_name: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetUsersResponse {
  success: boolean;
  count: number;
  data: UserManagement[];
}

export interface GetUserResponse {
  success: boolean;
  data: UserManagement;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  phone: string;
  password: string;
  role_id: string;
  role_name?: string;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  phone?: string;
  role_id?: string;
  role_name?: string;
}

export interface CreateUserResponse {
  success: boolean;
  message: string;
  data: UserManagement;
}

export interface UpdateUserResponse {
  success: boolean;
  message: string;
  data: UserManagement;
}

export interface DeleteUserResponse {
  success: boolean;
  message: string;
}

// Role Interfaces
export interface Role {
  _id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetRolesResponse {
  success: boolean;
  count: number;
  data: Role[];
}

// Error Messages Interfaces
export interface ErrorMessage {
  _id?: string;
  error_id: string;
  campaign_id: string;
  message: string;
  createdAt: string;
  updatedAt: string;
  error: {
    name: string;
    type: number;
  };
}

export interface GetErrorMessagesResponse {
  success: boolean;
  count: number;
  data: ErrorMessage[];
}

export interface UpdateErrorMessageRequest {
  message: string;
}

export interface UpdateErrorMessageResponse {
  success: boolean;
  data: ErrorMessage;
}

export interface BulkUpdateErrorMessagesItem {
  id: string;
  message: string;
}

export interface BulkUpdateErrorMessagesRequest {
  updates: BulkUpdateErrorMessagesItem[];
}

export interface BulkUpdateErrorMessagesResponse {
  success: boolean;
  updatedCount: number;
  data: ErrorMessage[];
}

// Verify Code Interfaces
export interface VerifyCodeRequest {
  prize_winner: string;
  phone: string;
  api_key: string;
  code: string[];
}

export interface VerifyCodeResponse {
  success: boolean;
  message?: string;
  data?: any;
}

// Campaign Interfaces
export type CampaignType = "normal" | "voucher";

export interface Campaign {
  _id?: string;
  name: string;
  description: string;
  product_name: string;
  gift_name: string;
  total_gift: number;
  rate_code: number;
  rate_gift: number;
  start_date: string;
  end_date: string;
  landing_page_link: string;
  batch: string;
  api_key: string;
  status: number;
  type?: CampaignType;
  win_limit?: number;
  createdAt: string;
  updatedAt: string;
  num_code_used?: number;
  num_win_prizes?: number;
}

export interface GetCampaignsResponse {
  success: boolean;
  count?: number;
  data: Campaign[];
}

export interface GetCampaignResponse {
  name: string;
  description: string;
  product_name: string;
  gift_name: string;
  total_gift: number;
  rate_code: number;
  rate_gift: number;
  start_date: string;
  end_date: string;
  landing_page_link: string;
  batch: string;
  api_key: string;
  status: number;
  type?: CampaignType;
  createdAt: string;
  updatedAt: string;
}

// Dashboard Top campaigns
export interface CampaignsTopStatItem {
  _id: string;
  name: string;
  description: string;
  product_name: string;
  gift_name: string;
  total_gift: number;
  rate_code: number;
  rate_gift: number;
  start_date: string;
  end_date: string;
  landing_page_link: string;
  batch: string;
  api_key: string;
  status: number;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignsTopStatResponse {
  success: boolean;
  data: {
    total_campaign: number;
    ended_campaign: number;
    ongoing_campaign: number;
    top_5_campaign: CampaignsTopStatItem[];
  };
}

export interface CreateCampaignRequest {
  name: string;
  description: string;
  product_name: string;
  gift_name: string;
  total_gift: number;
  rate_code: number;
  rate_gift: number;
  start_date: string;
  end_date: string;
  landing_page_link: string;
  batch: string;
  api_key: string;
  status: number;
  type?: CampaignType;
  win_limit?: number;
}

export interface UpdateCampaignRequest {
  name?: string;
  description?: string;
  product_name?: string;
  gift_name?: string;
  total_gift?: number;
  rate_code?: number;
  rate_gift?: number;
  start_date?: string;
  end_date?: string;
  landing_page_link?: string;
  batch?: string;
  api_key?: string;
  status?: number;
  type?: CampaignType;
  win_limit?: number;
}

export interface CampaignPartnerLink {
  _id: string;
  name: string;
  status: "active" | "inactive";
  link_id: string;
  linked_at: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetCampaignPartnersResponse {
  success: boolean;
  count?: number;
  data: CampaignPartnerLink[];
}

export interface CampaignPartner {
  _id: string;
  campaign_id: string;
  partner_id: string;
  createdAt: string;
  updatedAt: string;
}

export interface AttachCampaignPartnerResponse {
  success: boolean;
  data: CampaignPartner;
}

export interface DetachCampaignPartnerResponse {
  success: boolean;
  message: string;
}

export interface CreateCampaignResponse {
  success: boolean;
  data: Campaign;
  codes_generated: number;
}

export interface UpdateCampaignResponse {
  success: boolean;
  data: Campaign;
}

export interface DeleteCampaignResponse {
  success: boolean;
  message: string;
}

export interface UpdateCampaignStatusResponse {
  success: boolean;
  message: string;
  data: Campaign;
}

// Partner Interfaces
export interface PartnerFulfillment {
  /** Gift page: filter shops by province/district */
  require_location_select?: boolean;
  /** Gift page: online-only, require user delivery address */
  require_user_address?: boolean;
}

export interface Partner {
  _id: string;
  name: string;
  status: "active" | "inactive";
  createdAt: string;
  updatedAt: string;
  /** Public vanity path, e.g. long-chau → pregnavie.vn/long-chau */
  slug?: string | null;
  /** Stable token for partner deep-link redeem (legacy / fallback) */
  partner_token?: string | null;
  /** Gift-page exception rules */
  fulfillment?: PartnerFulfillment | null;
  /** Computed: landing URL + /slug (or ?partner_token= fallback), or null */
  campaign_link?: string | null;
  campaign_id?: string | null;
  campaign_name?: string | null;
}

export interface GetPartnersResponse {
  success: boolean;
  count?: number;
  data: Partner[];
}

export interface GetPartnerResponse {
  success: boolean;
  data: Partner;
}

export interface CreatePartnerRequest {
  name: string;
  status?: "active" | "inactive";
  /** Optional vanity slug; auto-generated from name if omitted */
  slug?: string;
  fulfillment?: PartnerFulfillment;
}

export interface UpdatePartnerRequest {
  name?: string;
  status?: "active" | "inactive";
  slug?: string;
  fulfillment?: PartnerFulfillment;
}

export interface CreatePartnerResponse {
  success: boolean;
  data: Partner;
}

export interface UpdatePartnerResponse {
  success: boolean;
  data: Partner;
}

export interface DeletePartnerResponse {
  success: boolean;
  message: string;
}

// Partner Voucher Interfaces
export type PartnerVoucherStatus =
  | "available"
  | "issued"
  | "redeemed"
  | "revoked";

export interface PartnerVoucher {
  _id: string;
  partner_id: string;
  code: string;
  status: PartnerVoucherStatus;
  issued_at?: string;
  redeemed_at?: string;
  redeemed_by?: string;
  import_batch?: string;
  createdAt: string;
  updatedAt: string;
  partner_name?: string | null;
  partner_status?: "active" | "inactive" | null;
  /** Customer name from voucher exchange (issued/redeemed) */
  prize_winner?: string | null;
  /** Customer phone from voucher exchange (issued/redeemed) */
  phone?: string | null;
}

export interface PartnerVoucherSummary {
  total: number;
  available: number;
  issued: number;
  redeemed: number;
  revoked: number;
}

export interface GetAllPartnerVouchersParams {
  status?: string;
  partner_id?: string;
  code?: string;
  import_batch?: string;
  page?: number;
  limit?: number;
}

export interface GetAllPartnerVouchersResponse {
  success: boolean;
  summary: PartnerVoucherSummary;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  data: PartnerVoucher[];
}

export interface GetPartnerVouchersResponse {
  success: boolean;
  count?: number;
  data: PartnerVoucher[];
}

export interface ImportPartnerVouchersResponse {
  success: boolean;
  count?: number;
  data: PartnerVoucher[];
  message?: string;
  errors?: Array<{ row: number; message: string }>;
}

export interface RedeemPartnerVoucherResponse {
  success: boolean;
  data: PartnerVoucher;
}

// Partner Location Interfaces
export type PartnerLocationStatus = "active" | "inactive";

export interface PartnerLocation {
  _id: string;
  partner_id: string;
  name: string;
  address: string;
  phone?: string;
  province?: string;
  district?: string;
  ward?: string;
  status: PartnerLocationStatus;
  import_batch?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetPartnerLocationsResponse {
  success: boolean;
  count?: number;
  data: PartnerLocation[];
}

export interface ImportPartnerLocationsResponse {
  success: boolean;
  count?: number;
  data: PartnerLocation[];
  message?: string;
  errors?: Array<{ row: number; message: string }>;
}

export interface DeletePartnerLocationResponse {
  success: boolean;
  message: string;
}

// Voucher Exchange Interfaces
export interface VoucherExchange {
  _id: string;
  campaign_id: string;
  partner_id: string;
  partner_voucher_id: string;
  prize_winner: string;
  phone: string;
  codes: string;
  code_ids?: string[];
  ip_local?: string;
  voucher_code?: string | null;
  voucher_status?: PartnerVoucherStatus | null;
  partner_name?: string | null;
  campaign_name?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GetVoucherExchangesResponse {
  success: boolean;
  count?: number;
  data: VoucherExchange[];
}

export interface SwitchVoucherPartnerRequest {
  new_partner_id: string;
  reason: string;
}

export interface SwitchVoucherPartnerResponse {
  success: boolean;
  message?: string;
  data: VoucherExchange;
}

// Code Management Interfaces
export interface CreateCodeRequest {
  batch: string;
  num_code: number;
}

export interface CreateCodeResponse {
  success: boolean;
  message: string;
  data?: {
    batch: string;
    codes_created: number;
    codes: string[];
  };
  // Alternative structure that might be returned
  codes_created?: number;
  batch?: string;
  codes?: string[];
}

export interface CodeItem {
  _id: string;
  campaign_id: string | null;
  batch_id: string;
  batch_name?: string; // Add batch_name field
  code: string;
  is_exported: number;
  is_used: number;
  date_used: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GetCodesPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface GetCodesResponse {
  success: boolean;
  total_code: number;
  total_code_used: number;
  total_code_not_used: number;
  pagination: GetCodesPagination;
  data: CodeItem[];
}

// Delete codes interfaces
export interface DeleteCodesRequest {
  num_code: number;
}

export interface DeleteCodesResponse {
  success: boolean;
  deleted_count: number;
  message: string;
}

// Delete single code interface
export interface DeleteCodeResponse {
  success: boolean;
  message: string;
}

// Batch Interfaces
export interface Batch {
  _id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetBatchesResponse {
  success: boolean;
  count: number;
  data: Batch[];
}

// Access Data Interfaces
export interface AccessDataItem {
  _id: string;
  id: string;
  type: "link" | "button" | "explore";
  month: number;
  link: string;
  timestamp: string;
  createdAt: string;
  updatedAt: string;
}

export interface AccessDataApiResponse {
  success: boolean;
  data: AccessDataItem[];
}

export interface LinkAccessData {
  date: string;
  accessCount: number;
}

export interface Video3DViewsData {
  date: string;
  views: number;
}

export interface GetAccessDataResponse {
  linkAccess: LinkAccessData[];
  video3DViews: Video3DViewsData[];
  exploreAccess?: LinkAccessData[];
}

// Win Prizes Interfaces
export interface WinPrize {
  _id: string;
  campaign_id: string;
  prize_winner: string;
  phone: string;
  winning_day: string;
  address?: string;
  ip_local?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetWinPrizesResponse {
  success: boolean;
  count: number;
  data: WinPrize[];
}

export interface ApiError {
  message: string;
  status?: number;
}

// API Service Class
class ApiService {
  private baseUrl: string;
  private token: string | null = null;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<RefreshTokenResponse> | null = null;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (error: any) => void;
  }> = [];
  private autoRefreshTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    // Get token from localStorage on initialization
    this.token = localStorage.getItem("auth_token");

    // Start auto refresh timer if token exists
    if (this.token) {
      this.startAutoRefresh();
    }
  }

  // Set authentication token
  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem("auth_token", token);
      this.startAutoRefresh();
    } else {
      localStorage.removeItem("auth_token");
      this.stopAutoRefresh();
    }
  }

  // Get current token
  getToken(): string | null {
    return this.token || localStorage.getItem("auth_token");
  }

  // Build headers for API requests
  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      accept: "application/json",
    };

    if (includeAuth && this.getToken()) {
      headers["Authorization"] = `Bearer ${this.getToken()}`;
    }

    return headers;
  }

  // Generic API request method
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    includeAuth: boolean = true
  ): Promise<T> {
    const url = buildApiUrl(endpoint);
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(includeAuth),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // If 401 and not refresh token endpoint, try to refresh token
        if (
          response.status === 401 &&
          includeAuth &&
          endpoint !== API_CONFIG.ENDPOINTS.AUTH.REFRESH &&
          endpoint !== API_CONFIG.ENDPOINTS.AUTH.LOGOUT // Don't retry logout on 401
        ) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({
              resolve: () => {
                // Retry the original request with new token
                this.retryRequest(url, config).then(resolve).catch(reject);
              },
              reject,
            });

            if (!this.isRefreshing) {
              this.refreshToken()
                .then(() => {
                  this.processQueue(null);
                })
                .catch((refreshError) => {
                  console.error(
                    "Token refresh failed, redirecting to login:",
                    refreshError
                  );
                  // Clear token and redirect to login
                  this.setToken(null);
                  // Dispatch custom event to notify app about auth failure
                  window.dispatchEvent(new CustomEvent("auth-failed"));
                  this.processQueue(refreshError);
                });
            }
          });
        }

        const errorData = await response.json().catch(() => ({}));

        // Convert error messages to Vietnamese
        let errorMessage = this.translateErrorMessage(
          errorData.message,
          response.status
        );

        // Create error object that preserves validation errors
        const error = new Error(errorMessage);
        (error as any).errors = errorData.errors;
        (error as any).message = errorMessage;
        throw error;
      }

      return await response.json();
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  // Translate error messages to Vietnamese
  private translateErrorMessage(
    originalMessage: string,
    status: number
  ): string {
    // Common error messages
    const errorTranslations: { [key: string]: string } = {
      // Authentication errors
      "Invalid credentials": "Email hoặc mật khẩu không chính xác",
      "User not found": "Email chưa được đăng ký trong hệ thống",
      "Email already exists": "Email đã được sử dụng",
      "Password is required": "Mật khẩu là bắt buộc",
      "Email is required": "Email là bắt buộc",
      "Invalid email format": "Định dạng email không hợp lệ",
      "Password too short": "Mật khẩu quá ngắn",
      "Please provide a valid phone number": "Số điện thoại không hợp lệ",
      "Phone number is required": "Số điện thoại là bắt buộc",
      "Invalid phone format": "Định dạng số điện thoại không hợp lệ",
      "Token expired": "Token đã hết hạn",
      "Invalid token": "Phiên đăng nhập không hợp lệ",
      "Access denied": "Không có quyền truy cập",
      Unauthorized: "Chưa đăng nhập",
      Forbidden: "Không có quyền thực hiện hành động này",
      "Account not found": "Tài khoản không tồn tại",
      "Wrong password": "Mật khẩu không đúng",
      "Account not activated": "Tài khoản chưa được kích hoạt",

      // Password reset errors
      "OTP expired": "Mã OTP đã hết hạn",
      "Invalid OTP": "Mã OTP không chính xác",
      "OTP not found": "Không tìm thấy mã OTP",
      "Too many OTP attempts": "Quá nhiều lần nhập sai mã OTP",

      // Server errors
      "Internal server error": "Lỗi máy chủ nội bộ",
      "Service unavailable": "Dịch vụ tạm thời không khả dụng",
      "Database connection failed": "Lỗi kết nối cơ sở dữ liệu",

      // Network errors
      "Network error": "Lỗi kết nối mạng",
      "Request timeout": "Yêu cầu quá thời gian chờ",
      "Connection refused": "Kết nối bị từ chối",

      // Code management errors
      "Batch already exists": "Batch đã tồn tại",
      "Invalid batch name": "Tên batch không hợp lệ",
      "Invalid number of codes": "Số lượng code không hợp lệ",
      "Too many codes requested": "Số lượng code yêu cầu quá lớn",
      "Batch not found": "Không tìm thấy batch",
      "Code generation failed": "Không thể tạo code",
    };

    // Status code based messages
    const statusMessages: { [key: number]: string } = {
      400: "Yêu cầu không hợp lệ",
      401: "Email hoặc mật khẩu không chính xác",
      403: "Không có quyền truy cập",
      404: "Không tìm thấy tài nguyên",
      409: "Dữ liệu đã tồn tại",
      422: "Dữ liệu không hợp lệ",
      429: "Quá nhiều yêu cầu, vui lòng thử lại sau",
      500: "Lỗi máy chủ nội bộ",
      502: "Lỗi cổng kết nối",
      503: "Dịch vụ tạm thời không khả dụng",
      504: "Hết thời gian chờ phản hồi",
    };

    // Try to translate the original message first
    if (originalMessage && errorTranslations[originalMessage]) {
      return errorTranslations[originalMessage];
    }

    // If we have an original message but no translation, return it as is
    if (originalMessage) {
      return originalMessage;
    }

    // Fall back to status code message only if no original message
    if (statusMessages[status]) {
      return statusMessages[status];
    }

    // Default message
    return "Đã xảy ra lỗi không xác định";
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>(
      API_CONFIG.ENDPOINTS.AUTH.LOGIN,
      {
        method: "POST",
        body: JSON.stringify(credentials),
      },
      false // Don't include auth token for login
    );

    // Store token after successful login
    this.setToken(response.token);
    return response;
  }

  async register(userData: RegisterRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>(
      API_CONFIG.ENDPOINTS.AUTH.REGISTER,
      {
        method: "POST",
        body: JSON.stringify(userData),
      },
      false // Don't include auth token for register
    );

    // Store token after successful registration
    this.setToken(response.token);
    return response;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.request<GetUserResponse>(
      API_CONFIG.ENDPOINTS.AUTH.ME,
      {
        method: "GET",
      }
    );
    return response.user;
  }

  async updateProfile(
    data: UpdateProfileRequest
  ): Promise<UpdateProfileResponse> {
    return this.request<UpdateProfileResponse>(
      API_CONFIG.ENDPOINTS.USER_PROFILE,
      {
        method: "PUT",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async changePassword(
    passwordData: ChangePasswordRequest
  ): Promise<{ success: boolean; message: string }> {
    return await this.request<{ success: boolean; message: string }>(
      API_CONFIG.ENDPOINTS.AUTH.CHANGE_PASSWORD,
      {
        method: "POST",
        body: JSON.stringify(passwordData),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async logout(): Promise<{ message: string }> {
    // Don't call logout API if we don't have a valid token
    const token = this.getToken();
    if (!token) {
      // Just clear local state if no token
      this.setToken(null);
      return { message: "Logged out successfully" };
    }

    try {
      const response = await this.request<{ message: string }>(
        API_CONFIG.ENDPOINTS.AUTH.LOGOUT,
        {
          method: "POST",
        }
      );

      // Clear token after successful logout
      this.setToken(null);
      return response;
    } catch (error) {
      // Even if logout API fails, clear the token locally
      console.warn("Logout API failed, clearing token locally:", error);
      this.setToken(null);
      return { message: "Logged out successfully" };
    }
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return await this.refreshPromise;
    }

    // Start refresh process
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();

    try {
      const response = await this.refreshPromise;
      return response;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<RefreshTokenResponse> {
    const response = await this.request<RefreshTokenResponse>(
      API_CONFIG.ENDPOINTS.AUTH.REFRESH,
      {
        method: "POST",
      },
      false // Don't include auth header for refresh token
    );

    // Update token after successful refresh
    if (response.success && response.token) {
      this.setToken(response.token);
    }

    return response;
  }

  private processQueue(error: any) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(undefined);
      }
    });

    this.failedQueue = [];
  }

  private async retryRequest(url: string, config: RequestInit): Promise<any> {
    const retryConfig: RequestInit = {
      ...config,
      headers: {
        ...this.getHeaders(true),
        ...config.headers,
      },
    };

    const retryResponse = await fetch(url, retryConfig);

    if (!retryResponse.ok) {
      const errorData = await retryResponse.json().catch(() => ({}));
      let errorMessage = this.translateErrorMessage(
        errorData.message,
        retryResponse.status
      );
      throw new Error(errorMessage);
    }

    return await retryResponse.json();
  }

  // Auto refresh token methods
  private startAutoRefresh() {
    this.stopAutoRefresh(); // Clear existing timer

    // Refresh token every 50 minutes (tokens typically expire in 1 hour)
    this.autoRefreshTimer = setInterval(async () => {
      try {
        if (this.token && !this.isRefreshing) {
          console.log("Auto refreshing token...");
          await this.refreshToken();
          console.log("Token refreshed successfully");
        }
      } catch (error) {
        console.error("Auto refresh failed:", error);
        // If auto refresh fails, clear token
        this.setToken(null);
      }
    }, 50 * 60 * 1000); // 50 minutes
  }

  private stopAutoRefresh() {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  // Password reset methods
  async sendOTP(email: string): Promise<{ message: string }> {
    return await this.request<{ message: string }>(
      API_CONFIG.ENDPOINTS.PASSWORD_RESET.SEND_OTP,
      {
        method: "POST",
        body: JSON.stringify({ email }),
      },
      false
    );
  }

  async verifyOTP(email: string, otp: string): Promise<{ message: string }> {
    return await this.request<{ message: string }>(
      API_CONFIG.ENDPOINTS.PASSWORD_RESET.VERIFY_OTP,
      {
        method: "POST",
        body: JSON.stringify({ email, otp }),
      },
      false
    );
  }

  async resetPassword(
    email: string,
    otp: string,
    newPassword: string
  ): Promise<{ message: string }> {
    return await this.request<{ message: string }>(
      API_CONFIG.ENDPOINTS.PASSWORD_RESET.RESET_PASSWORD,
      {
        method: "POST",
        body: JSON.stringify({ email, otp, newPassword }),
      },
      false
    );
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return await this.request<{ status: string }>(
      API_CONFIG.ENDPOINTS.HEALTH,
      {
        method: "GET",
      },
      false
    );
  }

  // Get API info
  async getApiInfo(): Promise<any> {
    return await this.request<any>(
      API_CONFIG.ENDPOINTS.INFO,
      {
        method: "GET",
      },
      false
    );
  }

  // User Management Methods
  async getUsers(): Promise<GetUsersResponse> {
    return await this.request<GetUsersResponse>(API_CONFIG.ENDPOINTS.USERS, {
      method: "GET",
    });
  }

  async getRoles(): Promise<GetRolesResponse> {
    return await this.request<GetRolesResponse>(API_CONFIG.ENDPOINTS.ROLES, {
      method: "GET",
    });
  }

  async getUserById(id: string): Promise<GetUserResponse> {
    return await this.request<GetUserResponse>(
      API_CONFIG.ENDPOINTS.USER_BY_ID(id),
      {
        method: "GET",
      }
    );
  }

  async createUser(userData: CreateUserRequest): Promise<CreateUserResponse> {
    return await this.request<CreateUserResponse>(API_CONFIG.ENDPOINTS.USERS, {
      method: "POST",
      body: JSON.stringify(userData),
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async updateUser(
    id: string,
    userData: UpdateUserRequest
  ): Promise<UpdateUserResponse> {
    return await this.request<UpdateUserResponse>(
      API_CONFIG.ENDPOINTS.USER_BY_ID(id),
      {
        method: "PUT",
        body: JSON.stringify(userData),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async deleteUser(id: string): Promise<DeleteUserResponse> {
    return await this.request<DeleteUserResponse>(
      API_CONFIG.ENDPOINTS.USER_BY_ID(id),
      {
        method: "DELETE",
      }
    );
  }

  // Error Messages methods
  async getErrorMessages(
    campaignId?: string
  ): Promise<GetErrorMessagesResponse> {
    const url = campaignId
      ? `${API_CONFIG.ENDPOINTS.ERROR_MESSAGES}?campaign_id=${campaignId}`
      : API_CONFIG.ENDPOINTS.ERROR_MESSAGES;
    return await this.request<GetErrorMessagesResponse>(url);
  }

  async updateErrorMessage(
    id: string,
    errorData: UpdateErrorMessageRequest
  ): Promise<UpdateErrorMessageResponse> {
    return await this.request<UpdateErrorMessageResponse>(
      API_CONFIG.ENDPOINTS.ERROR_MESSAGE_BY_ID(id),
      {
        method: "PUT",
        body: JSON.stringify(errorData),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async updateErrorMessagesBulk(
    payload: BulkUpdateErrorMessagesRequest
  ): Promise<BulkUpdateErrorMessagesResponse> {
    const bulkEndpoint = `${API_CONFIG.ENDPOINTS.ERROR_MESSAGES}/bulk`;
    return await this.request<BulkUpdateErrorMessagesResponse>(bulkEndpoint, {
      method: "PUT",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  // Verify Code
  async verifyCode(payload: VerifyCodeRequest): Promise<VerifyCodeResponse> {
    return await this.request<VerifyCodeResponse>(
      API_CONFIG.ENDPOINTS.VERIFY_CODE,
      {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  // Campaign methods
  async getCampaigns(): Promise<GetCampaignsResponse> {
    return await this.request<GetCampaignsResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGNS
    );
  }

  async getCampaign(id: string): Promise<GetCampaignResponse> {
    return await this.request<GetCampaignResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGN_BY_ID(id)
    );
  }

  async createCampaign(
    campaignData: CreateCampaignRequest
  ): Promise<CreateCampaignResponse> {
    return await this.request<CreateCampaignResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGNS,
      {
        method: "POST",
        body: JSON.stringify(campaignData),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async updateCampaign(
    id: string,
    campaignData: UpdateCampaignRequest
  ): Promise<UpdateCampaignResponse> {
    return await this.request<UpdateCampaignResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGN_BY_ID(id),
      {
        method: "PUT",
        body: JSON.stringify(campaignData),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async deleteCampaign(id: string): Promise<DeleteCampaignResponse> {
    return await this.request<DeleteCampaignResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGN_BY_ID(id),
      {
        method: "DELETE",
      }
    );
  }

  async updateCampaignStatus(
    id: string,
    status: number
  ): Promise<UpdateCampaignStatusResponse> {
    console.log(`API: Updating campaign ${id} status to ${status}`);
    console.log(`API: Request body:`, JSON.stringify({ status }));

    return await this.request<UpdateCampaignStatusResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGN_STATUS(id),
      {
        method: "PUT",
        body: JSON.stringify({ status }),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  // Win Prizes methods
  async getWinPrizes(
    campaignId?: string,
    sort?: string,
    search?: string
  ): Promise<GetWinPrizesResponse> {
    const params = new URLSearchParams();

    if (campaignId) {
      params.append("campaign_id", campaignId);
    }

    if (sort) {
      params.append("sort", sort);
    }

    if (search) {
      params.append("search", search);
    }

    const url = params.toString()
      ? `${API_CONFIG.ENDPOINTS.WIN_PRIZES}?${params.toString()}`
      : API_CONFIG.ENDPOINTS.WIN_PRIZES;

    return await this.request<GetWinPrizesResponse>(url, {
      method: "GET",
    });
  }

  async exportWinPrizes(
    campaignId?: string
  ): Promise<{ blob: Blob; filename: string }> {
    // Thử với query parameter campaign_id
    const baseUrl = `${API_CONFIG.ENDPOINTS.WIN_PRIZES_EXPORT}/all`;
    const url = campaignId ? `${baseUrl}?campaign_id=${campaignId}` : baseUrl;

    const response = await fetch(buildApiUrl(url), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
        Accept: "*/*",
      },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Use timestamp-based filename to avoid BE header issues
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19);
    const filename = `win-prizes-export-${timestamp}.xlsx`;

    return { blob, filename };
  }

  async getTopCampaignsStats(): Promise<CampaignsTopStatResponse> {
    return await this.request<CampaignsTopStatResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGNS_STAT_TOP,
      {
        method: "GET",
      }
    );
  }

  async exportCampaignCodes(
    type: "all" | "new",
    campaignId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const endpoint =
      type === "all"
        ? API_CONFIG.ENDPOINTS.CAMPAIGNS_EXPORT_ALL
        : API_CONFIG.ENDPOINTS.CAMPAIGNS_EXPORT_NEW;

    const url = `${endpoint}?campaign_id=${campaignId}`;

    const response = await fetch(buildApiUrl(url), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.getToken()}`,
        Accept: "*/*",
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Export failed: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    const blob = await response.blob();

    // Use timestamp-based filename to avoid BE header issues
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19);
    const filename = `campaign-codes-${type}-${timestamp}.xlsx`;

    return { blob, filename };
  }

  // Code Management methods
  async createCode(codeData: CreateCodeRequest): Promise<CreateCodeResponse> {
    return await this.request<CreateCodeResponse>(API_CONFIG.ENDPOINTS.CODES, {
      method: "POST",
      body: JSON.stringify(codeData),
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async getCodes(
    page: number = 1,
    limit: number = 10,
    campaignId?: string,
    batchId?: string,
    isUsed?: string,
    startDateUsed?: string,
    endDateUsed?: string,
    startCreatedAt?: string,
    endCreatedAt?: string,
    search?: string
  ): Promise<GetCodesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (campaignId) {
      params.append("campaign_id", campaignId);
    }

    if (batchId) {
      params.append("batch_id", batchId);
    }

    if (isUsed) {
      params.append("is_used", isUsed);
    }

    if (startDateUsed) {
      params.append("start_date_used", startDateUsed);
    }

    if (endDateUsed) {
      params.append("end_date_used", endDateUsed);
    }

    if (startCreatedAt) {
      params.append("start_createdAt", startCreatedAt);
    }

    if (endCreatedAt) {
      params.append("end_createdAt", endCreatedAt);
    }

    if (search) {
      params.append("code", search);
    }

    const url = `${API_CONFIG.ENDPOINTS.CODES}?${params.toString()}`;

    return await this.request<GetCodesResponse>(url, {
      method: "GET",
    });
  }

  async exportCodes(): Promise<Blob> {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CODES_EXPORT_NOT_USED}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Export failed: ${response.status} ${response.statusText}`
      );
    }

    return await response.blob();
  }

  async exportCodesByBatchAndDate(
    batchId: string,
    createdDate: string
  ): Promise<Blob> {
    const params = new URLSearchParams({
      batch_id: batchId,
    });

    const response = await fetch(
      `${API_CONFIG.BASE_URL}${
        API_CONFIG.ENDPOINTS.CODES_EXPORT_NOT_USED
      }?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(
          "Export failed: 404 - Batch not found or no codes available"
        );
      }
      throw new Error(
        `Export failed: ${response.status} ${response.statusText}`
      );
    }

    // Check if response is JSON (error) or blob (success)
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const errorData = await response.json();
      throw new Error(errorData.message || "No unused codes found");
    }

    return await response.blob();
  }

  async deleteCodes(numCode: number): Promise<DeleteCodesResponse> {
    return await this.request<DeleteCodesResponse>(
      API_CONFIG.ENDPOINTS.CODES_DELETE,
      {
        method: "POST",
        body: JSON.stringify({ num_code: numCode }),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async deleteCode(id: string): Promise<DeleteCodeResponse> {
    return await this.request<DeleteCodeResponse>(
      API_CONFIG.ENDPOINTS.CODE_BY_ID(id),
      {
        method: "DELETE",
      }
    );
  }

  // Batch methods
  async getBatches(): Promise<GetBatchesResponse> {
    return await this.request<GetBatchesResponse>(
      API_CONFIG.ENDPOINTS.BATCHES,
      {
        method: "GET",
      }
    );
  }

  // Partner methods
  async getPartners(params?: {
    status?: string;
    name?: string;
  }): Promise<GetPartnersResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.append("status", params.status);
    if (params?.name) query.append("name", params.name);
    const qs = query.toString();
    const endpoint = qs
      ? `${API_CONFIG.ENDPOINTS.PARTNERS}?${qs}`
      : API_CONFIG.ENDPOINTS.PARTNERS;
    return await this.request<GetPartnersResponse>(endpoint);
  }

  async getPartner(id: string): Promise<GetPartnerResponse> {
    return await this.request<GetPartnerResponse>(
      API_CONFIG.ENDPOINTS.PARTNER_BY_ID(id)
    );
  }

  async createPartner(
    data: CreatePartnerRequest
  ): Promise<CreatePartnerResponse> {
    return await this.request<CreatePartnerResponse>(
      API_CONFIG.ENDPOINTS.PARTNERS,
      {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async updatePartner(
    id: string,
    data: UpdatePartnerRequest
  ): Promise<UpdatePartnerResponse> {
    return await this.request<UpdatePartnerResponse>(
      API_CONFIG.ENDPOINTS.PARTNER_BY_ID(id),
      {
        method: "PUT",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async deletePartner(id: string): Promise<DeletePartnerResponse> {
    return await this.request<DeletePartnerResponse>(
      API_CONFIG.ENDPOINTS.PARTNER_BY_ID(id),
      {
        method: "DELETE",
      }
    );
  }

  async getAllPartnerVouchers(
    params?: GetAllPartnerVouchersParams
  ): Promise<GetAllPartnerVouchersResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.append("status", params.status);
    if (params?.partner_id) query.append("partner_id", params.partner_id);
    if (params?.code) query.append("code", params.code);
    if (params?.import_batch) query.append("import_batch", params.import_batch);
    if (params?.page) query.append("page", String(params.page));
    if (params?.limit) query.append("limit", String(params.limit));

    const qs = query.toString();
    const endpoint = qs
      ? `${API_CONFIG.ENDPOINTS.ALL_PARTNER_VOUCHERS}?${qs}`
      : API_CONFIG.ENDPOINTS.ALL_PARTNER_VOUCHERS;

    return await this.request<GetAllPartnerVouchersResponse>(endpoint);
  }

  async getPartnerVouchers(
    partnerId: string,
    status?: string
  ): Promise<GetPartnerVouchersResponse> {
    const endpoint = status
      ? `${API_CONFIG.ENDPOINTS.PARTNER_VOUCHERS(partnerId)}?status=${encodeURIComponent(status)}`
      : API_CONFIG.ENDPOINTS.PARTNER_VOUCHERS(partnerId);
    return await this.request<GetPartnerVouchersResponse>(endpoint);
  }

  private async uploadMultipartFile<T>(
    endpoint: string,
    file: File,
    fieldName = "file"
  ): Promise<T> {
    const form = new FormData();
    form.append(fieldName, file);

    const headers: HeadersInit = {
      accept: "application/json",
    };
    const token = this.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(buildApiUrl(endpoint), {
      method: "POST",
      headers,
      body: form,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = this.translateErrorMessage(
        errorData.message,
        response.status
      );
      const error = new Error(errorMessage);
      (error as any).errors = errorData.errors;
      throw error;
    }

    return await response.json();
  }

  async importPartnerVouchers(
    partnerId: string,
    file: File
  ): Promise<ImportPartnerVouchersResponse> {
    return this.uploadMultipartFile<ImportPartnerVouchersResponse>(
      API_CONFIG.ENDPOINTS.PARTNER_VOUCHERS_IMPORT(partnerId),
      file
    );
  }

  async redeemPartnerVoucher(
    partnerId: string,
    voucherId: string
  ): Promise<RedeemPartnerVoucherResponse> {
    return await this.request<RedeemPartnerVoucherResponse>(
      API_CONFIG.ENDPOINTS.PARTNER_VOUCHER_REDEEM(partnerId, voucherId),
      {
        method: "PATCH",
      }
    );
  }

  async getPartnerLocations(
    partnerId: string,
    params?: { status?: string; search?: string }
  ): Promise<GetPartnerLocationsResponse> {
    const query = new URLSearchParams();
    if (params?.status) query.append("status", params.status);
    if (params?.search) query.append("search", params.search);
    const qs = query.toString();
    const endpoint = qs
      ? `${API_CONFIG.ENDPOINTS.PARTNER_LOCATIONS(partnerId)}?${qs}`
      : API_CONFIG.ENDPOINTS.PARTNER_LOCATIONS(partnerId);
    return await this.request<GetPartnerLocationsResponse>(endpoint);
  }

  async importPartnerLocations(
    partnerId: string,
    file: File
  ): Promise<ImportPartnerLocationsResponse> {
    return this.uploadMultipartFile<ImportPartnerLocationsResponse>(
      API_CONFIG.ENDPOINTS.PARTNER_LOCATIONS_IMPORT(partnerId),
      file
    );
  }

  async deletePartnerLocation(
    partnerId: string,
    locationId: string
  ): Promise<DeletePartnerLocationResponse> {
    return await this.request<DeletePartnerLocationResponse>(
      API_CONFIG.ENDPOINTS.PARTNER_LOCATION_BY_ID(partnerId, locationId),
      {
        method: "DELETE",
      }
    );
  }

  // Campaign Partner methods
  async getCampaignPartners(
    id: string
  ): Promise<GetCampaignPartnersResponse> {
    return await this.request<GetCampaignPartnersResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGN_PARTNERS(id)
    );
  }

  async attachCampaignPartner(
    id: string,
    partnerId: string
  ): Promise<AttachCampaignPartnerResponse> {
    return await this.request<AttachCampaignPartnerResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGN_PARTNERS(id),
      {
        method: "POST",
        body: JSON.stringify({ partner_id: partnerId }),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  async detachCampaignPartner(
    id: string,
    partnerId: string
  ): Promise<DetachCampaignPartnerResponse> {
    return await this.request<DetachCampaignPartnerResponse>(
      API_CONFIG.ENDPOINTS.CAMPAIGN_PARTNER_BY_ID(id, partnerId),
      {
        method: "DELETE",
      }
    );
  }

  // Voucher Exchange methods
  async getVoucherExchanges(params?: {
    phone?: string;
    partner_id?: string;
    campaign_id?: string;
  }): Promise<GetVoucherExchangesResponse> {
    const query = new URLSearchParams();
    if (params?.phone) query.append("phone", params.phone);
    if (params?.partner_id) query.append("partner_id", params.partner_id);
    if (params?.campaign_id) {
      query.append("campaign_id", params.campaign_id);
    }
    const qs = query.toString();
    const endpoint = qs
      ? `${API_CONFIG.ENDPOINTS.VOUCHER_EXCHANGES}?${qs}`
      : API_CONFIG.ENDPOINTS.VOUCHER_EXCHANGES;
    return await this.request<GetVoucherExchangesResponse>(endpoint);
  }

  async switchVoucherPartner(
    exchangeId: string,
    data: SwitchVoucherPartnerRequest
  ): Promise<SwitchVoucherPartnerResponse> {
    return await this.request<SwitchVoucherPartnerResponse>(
      API_CONFIG.ENDPOINTS.VOUCHER_EXCHANGE_SWITCH_PARTNER(exchangeId),
      {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }

  // Access data methods
  async getAccessData(params?: {
    month?: number;
    startDate?: string;
    endDate?: string;
    type?: "link" | "button" | "explore";
  }): Promise<GetAccessDataResponse> {
    const baseUrl = buildApiUrl(API_CONFIG.ENDPOINTS.ACCESS);

    // Build query parameters (API filters by createdAt for date range)
    const queryParams = new URLSearchParams();

    if (params?.month !== undefined && params.month !== null) {
      queryParams.append("month", params.month.toString());
    }
    if (params?.startDate) {
      queryParams.append("startDate", params.startDate);
    }
    if (params?.endDate) {
      queryParams.append("endDate", params.endDate);
    }
    if (params?.type) {
      queryParams.append("type", params.type);
    }

    const accessUrl = queryParams.toString()
      ? `${baseUrl}?${queryParams.toString()}`
      : baseUrl;

    const response = await fetch(accessUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
        ...(this.getToken() ? { Authorization: `Bearer ${this.getToken()}` } : {}),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let errorMessage = this.translateErrorMessage(
        errorData.message,
        response.status
      );
      throw new Error(errorMessage);
    }

    const apiResponse: AccessDataApiResponse = await response.json();

    // Transform API data to chart format: group by date and count by type
    const linkAccessMap = new Map<string, number>();
    const videoViewsMap = new Map<string, number>();
    const exploreAccessMap = new Map<string, number>();

    (apiResponse.data || []).forEach((item) => {
      // Use timestamp as the event time for charting (YYYY-MM-DD)
      const date = new Date(item.timestamp).toISOString().split("T")[0];

      if (item.type === "link") {
        linkAccessMap.set(date, (linkAccessMap.get(date) || 0) + 1);
      } else if (item.type === "explore") {
        exploreAccessMap.set(date, (exploreAccessMap.get(date) || 0) + 1);
      } else if (item.type === "button") {
        videoViewsMap.set(date, (videoViewsMap.get(date) || 0) + 1);
      }
    });

    const linkAccess: LinkAccessData[] = Array.from(linkAccessMap.entries())
      .map(([date, accessCount]) => ({ date, accessCount }))
      .sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

    const video3DViews: Video3DViewsData[] = Array.from(videoViewsMap.entries())
      .map(([date, views]) => ({ date, views }))
      .sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

    const exploreAccess: LinkAccessData[] = Array.from(exploreAccessMap.entries())
      .map(([date, accessCount]) => ({ date, accessCount }))
      .sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

    return { linkAccess, video3DViews, exploreAccess };
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Export the class for testing or custom instances
export default ApiService;
